import {
  mdiInformation,
  mdiPencil,
  mdiPlay,
  mdiTriangleSmallDown,
} from "@mdi/js";
import { Button, Modal, Space, Tooltip, Typography } from "antd";
import { PermissionVm, RoleListVm } from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import { MaterialIcon } from "components/MaterialIcon";
import CheckboxDisplay from "components/ui/CheckboxDisplay";
import useTable from "hooks/useTable";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "widgets/table/CustomTable";
import { ColumnStorageName } from "widgets/table/table-settings/ColumnStorageName";
import { ColumnConfigParams, FilterMode } from "widgets/table/useTableUtils";
import styles from "./PermissionsAndRolesAssign.module.scss";
import clsx from "clsx";
import RoleSelect from "./RoleSelect/RoleSelect";
import { GetPermissionRoleRelationsRequestParams } from "./permissionsAndRolesAssign.store";
import { useBlocker } from "react-router-dom";
import useNavigationBlocker from "widgets/form/hooks/useNavigationBlocker";

const { confirm } = Modal;

type TransformedRelationType = Record<
  string,
  Record<string, boolean | undefined>
>;

const PermissionsAndRolesAssign = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isEditing, setEditing] = useState<boolean>(false);
  const [selectedRoles, setSelectedRoles] = useState<RoleListVm[]>([]);

  const {
    roles,
    relations,
    permissions,
    listParams,
    filterRoleId,
    allPermissions,
    isSaveDisabled,
  } = useSelector((state) => state.permissionsAndRolesAssign);

  const { getPermissionRoleRelations, updatePermissionRoleRelations } =
    useSelector((state) => state.loading.effects.permissionsAndRolesAssign);

  useEffect(() => {
    dispatch.permissionsAndRolesAssign.getAllPermissions();
  }, [dispatch.permissionsAndRolesAssign]);

  const changedValues = useRef<TransformedRelationType>({});

  const memoPermissions = useMemo(() => {
    return (
      permissions?.map((item) => ({
        ...item,
        id: item.name,
      })) ?? []
    );
  }, [permissions]);

  const filteredRoles = useMemo(
    () =>
      [...((selectedRoles?.length ? selectedRoles : roles) ?? [])]?.sort(
        (a, b) => {
          if (a.isBuiltIn && !b.isBuiltIn) {
            return -1;
          } else if (!a.isBuiltIn && b.isBuiltIn) {
            return 1;
          }
          return a.name && b.name ? a.name?.localeCompare(b.name) : 0;
        }
      ),
    [roles, selectedRoles]
  );

  const transformedRelations = useMemo(
    () =>
      relations?.reduce<TransformedRelationType>(
        (prev, { permission, roleId }) => {
          if (!permission || !roleId) {
            return prev;
          }

          if (!prev[permission]) {
            prev[permission] = { [roleId]: true };
          } else {
            prev[permission][roleId] = true;
          }

          return prev;
        },
        {}
      ),
    [relations]
  );

  const addOrRemoveRelation = useCallback(
    ({
      permission,
      roleId,
      checked,
    }: {
      permission: string;
      roleId: string;
      checked: boolean;
    }) => {
      if (checked === !!transformedRelations?.[permission]?.[roleId]) {
        delete changedValues.current[permission]?.[roleId];

        if (Object.keys(changedValues.current[permission]).length === 0) {
          delete changedValues.current[permission];
        }

        if (Object.keys(changedValues.current).length === 0) {
          dispatch.permissionsAndRolesAssign.setIsSaveDisabled(true);
        } else {
          dispatch.permissionsAndRolesAssign.setIsSaveDisabled(false);
        }

        return;
      }

      changedValues.current = {
        ...changedValues.current,
        [permission]: {
          ...(changedValues.current[permission] ?? {}),
          [roleId]: checked,
        },
      };

      if (Object.keys(changedValues.current).length !== 0) {
        dispatch.permissionsAndRolesAssign.setIsSaveDisabled(false);
      }
    },
    [dispatch.permissionsAndRolesAssign, transformedRelations]
  );

  const fetchTableData = useCallback(
    (requestParams: GetPermissionRoleRelationsRequestParams) => {
      return dispatch.permissionsAndRolesAssign.getPermissionRoleRelations(
        requestParams
      );
    },
    [dispatch.permissionsAndRolesAssign]
  );

  const resetChangedValues = useCallback(() => {
    changedValues.current = {};
    fetchTableData(listParams);
    dispatch.permissionsAndRolesAssign.reset();
    dispatch.permissionsAndRolesAssign.setIsSaveDisabled(true);
  }, [dispatch.permissionsAndRolesAssign, fetchTableData, listParams]);

  const handleSaveRelations = useCallback(async () => {
    const rolePermissionRelations = Object.entries(
      changedValues.current
    ).flatMap(([permission, roles]) =>
      Object.entries(roles).map(([roleId, relation]) => ({
        permission,
        roleId,
        relation,
      }))
    );

    if (rolePermissionRelations) {
      confirm({
        okText: t("common.confirmDeleteModal.yes"),
        okType: "danger",
        cancelText: t("common.confirmDeleteModal.no"),
        title: t(
          "rolesAndPermissionsSettings.permissionsAndRolesAssign.confirmSaveModal.title"
        ),
        onOk: async () => {
          await dispatch.permissionsAndRolesAssign.updatePermissionRoleRelations(
            {
              updateRolePermissionRelationsCommand: { rolePermissionRelations },
            }
          );
          setEditing(false);
          resetChangedValues();
        },
        onCancel: () => {
          resetChangedValues();
          setEditing(false);
        },
      });
    }
  }, [dispatch.permissionsAndRolesAssign, resetChangedValues, t]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      currentLocation.pathname !== nextLocation.pathname && !isSaveDisabled
    );
  });

  useNavigationBlocker({ blocker });

  const handleSortPermissionWithRole = useCallback(
    async (role: RoleListVm) => {
      dispatch.permissionsAndRolesAssign.setFilterRoleId(role.id || "");
      fetchTableData(listParams);
    },
    [dispatch.permissionsAndRolesAssign, fetchTableData, listParams]
  );

  const handleSortRolesWithPermission = useCallback(
    (permission: PermissionVm) => {
      const filteredRoleIds = relations
        ?.filter((relation) => relation.permission === permission.name)
        .map((item) => item.roleId);

      const filteredCheckedRoles =
        roles?.filter((role) => filteredRoleIds?.includes(role.id || "")) ?? [];

      setSelectedRoles(filteredCheckedRoles);
    },
    [roles, relations]
  );

  const columnParams = useMemo<ColumnConfigParams<PermissionVm>[]>(() => {
    return [
      {
        key: "permissions",
        dataIndex: "name",
        title: t("common.permissionName"),
        width: "16rem",
        fixed: "left",
        filterMode: FilterMode.FILTER_SELECT,
        filters: (allPermissions || []).map((item) => ({
          text: item.name || "",
          value: item.name || "",
        })),

        render: (_: any, record) => {
          return (
            <Space className={styles.cellWrapper}>
              <Space>
                <Tooltip
                  title={record.description}
                  placement="topLeft"
                  align={{ offset: [-40, 5] }}
                >
                  <MaterialIcon path={mdiInformation} />
                </Tooltip>
                <Space>
                  <Typography.Text className={styles.text}>
                    {record.name}
                  </Typography.Text>
                </Space>
              </Space>
              <Space>
                <Space>
                  <div
                    onClick={() => handleSortRolesWithPermission(record)}
                    className={styles.sortIcon}
                  >
                    <MaterialIcon path={mdiPlay} size={0.75} />
                  </div>
                </Space>
              </Space>
            </Space>
          );
        },
      },
      ...(filteredRoles ?? []).map((role) => {
        return {
          key: `permissionRole-${role.id}`,
          dataIndex: "name",
          width: "15rem",
          onHeaderCell: () => ({
            className: clsx({ [styles.builtIn]: role.isBuiltIn }),
          }),
          title: (
            <div className={styles.titleWrapper}>
              <Tooltip title={role.description}>
                <MaterialIcon path={mdiInformation} />
              </Tooltip>
              <div className={styles.title}>
                <Tooltip title={role.name} placement="topLeft">
                  {role.name}
                </Tooltip>
              </div>
              <div
                onClick={() => handleSortPermissionWithRole(role)}
                className={styles.sortIcon}
              >
                <MaterialIcon path={mdiTriangleSmallDown} size={1.1} />
              </div>
            </div>
          ),

          render: (permission: string) => (
            <Space className={styles.roleCell}>
              <CheckboxDisplay
                disabled={!isEditing}
                isEditing={isEditing}
                value={
                  role.id
                    ? {
                        ...transformedRelations,
                        ...changedValues.current,
                      }[permission]?.[role.id] ?? false
                    : false
                }
                onChange={(checked) => {
                  if (!role.id) {
                    return;
                  }

                  addOrRemoveRelation({
                    permission,
                    roleId: role.id,
                    checked,
                  });
                }}
              />
            </Space>
          ),
        };
      }),
      {
        key: "emptyCol",
        fixed: "right",
        render: () => <></>,
      },
    ];
  }, [
    addOrRemoveRelation,
    allPermissions,
    filteredRoles,
    handleSortPermissionWithRole,
    handleSortRolesWithPermission,
    isEditing,
    t,
    transformedRelations,
  ]);

  const customTableProps = useTable({
    fetchTableData,
    columnParams,
    list: memoPermissions,
    listParams,
    columnStorageName: ColumnStorageName.PERMISSIONS_AND_ROLES_ASSIGN,
    isResetCustomFilters: !!selectedRoles.length || !!filterRoleId,
    selectMode: "multiple",
    roleSelect: (
      <RoleSelect
        selectedRoles={selectedRoles}
        handleSelectedRoles={(e: RoleListVm[]) => {
          setSelectedRoles(e);
        }}
      />
    ),
    disableTableSettings: true,
    headerOptions: (
      <Space>
        {isEditing ? (
          <Space>
            <Button
              type="text"
              onClick={() => {
                if (!isSaveDisabled) {
                  confirm({
                    okText: t("common.confirmDeleteModal.yes"),
                    okType: "danger",
                    cancelText: t("common.confirmDeleteModal.no"),
                    title: t(
                      "rolesAndPermissionsSettings.permissionsAndRolesAssign.confirmCancelModal.title"
                    ),
                    onOk: async () => {
                      setEditing(false);
                      resetChangedValues();
                    },
                  });
                } else {
                  setEditing(false);
                }
              }}
            >
              {t("common.cancel")}
            </Button>

            <Button
              type="primary"
              loading={updatePermissionRoleRelations.loading}
              onClick={handleSaveRelations}
              disabled={isSaveDisabled}
            >
              {t("common.save")}
            </Button>
          </Space>
        ) : (
          <Button
            icon={<MaterialIcon path={mdiPencil} />}
            loading={updatePermissionRoleRelations.loading}
            onClick={() => setEditing(true)}
          >
            {t("common.edit")}
          </Button>
        )}
      </Space>
    ),
    resetStore: dispatch.permissionsAndRolesAssign.reset,
    resetListParams: () => {
      setSelectedRoles([]);
      dispatch.permissionsAndRolesAssign.setFilterRoleId(null);
      dispatch.permissionsAndRolesAssign.resetListParams();
    },
    loading: getPermissionRoleRelations.loading,
  });

  return (
    <CustomTable<PermissionVm>
      {...customTableProps}
      scrollX={"max-content"}
      className={styles.wrapper}
      selectable={false}
      pageHasTabs
    />
  );
};

export default PermissionsAndRolesAssign;
